/* eslint-disable @atlassian/relay/unused-fields */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { graphql, type PreloadedQuery, useFragment, usePreloadedQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import { DEFAULT_BRANDING_COLOR } from '../../constants';
import {
	NavigationStyleContext,
	type NavigationStyleContextType,
} from '../../controllers/navigation-style-context';
import type { HubAppProps } from '../../types';
import { HubContext } from '../../utils';
import { mapApiToNavigationOptions } from '../../utils/navigation-bar-branding';

import { type hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference$key } from './__generated__/hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference.graphql';
import { type hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference$key } from './__generated__/hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference.graphql';
import { type hubBootstrapWrapperQuery } from './__generated__/hubBootstrapWrapperQuery.graphql';

interface HubBootstrapProps extends HubAppProps {
	bootstrapQueryReference: hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference$key;
	children: React.ReactNode;
	cloudId: string;
	isInPreviewMode?: boolean;
	tenantContextsQueryReference: hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference$key;
}
interface HubBootstrapWrapperProps extends HubAppProps {
	children: React.ReactNode;
	cloudId: string;
	isInPreviewMode?: boolean;
	queryReference: PreloadedQuery<hubBootstrapWrapperQuery>;
}

type BootstrapPageMediaConfigInput =
	| {
			asapIssuer?: string | null;
			mediaCollectionName?: string | null;
			mediaToken?: string | null;
			mediaUrl?: string | null;
	  }
	| null
	| undefined;


/**
 * Narrows the page-scoped media config returned by the bootstrap fragment
 * (where every field is nullable due to `@skip(if: $skipPageMediaConfig)`) to
 * the non-nullable shape expected by `HubContext.pageMediaConfig`. Returns
 * `undefined` when any required field is missing so the caller can omit the
 * key from the context value entirely.
 */
const getPageMediaConfigFromBootstrap = (input: BootstrapPageMediaConfigInput) => {
	if (!input) {
		return undefined;
	}
	const { asapIssuer, mediaCollectionName, mediaToken, mediaUrl } = input;
	if (asapIssuer == null || mediaCollectionName == null || mediaToken == null || mediaUrl == null) {
		return undefined;
	}
	return { asapIssuer, mediaCollectionName, mediaToken, mediaUrl };
};

export const HubBootstrap = ({
	bootstrapQueryReference,
	tenantContextsQueryReference,
	children,
	cloudId,
	isInPreviewMode = false,
	...props
}: HubBootstrapProps): React.JSX.Element => {
	// `skipPageMediaConfig` defaults to `false` so callers that don't pass it
	// fall back to the original behavior of fetching pageMediaConfig in the
	// bootstrap. The wrapper query passes a value derived from the fix flag.
	const data = useFragment<hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference$key>(
		graphql`
			fragment hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference on HelpCenterQueryApi
			@argumentDefinitions(
				pageAri: { type: "ID", defaultValue: "" }
				skipPageMediaConfig: { type: "Boolean", defaultValue: false }
			) {
				hubMediaConfig(ari: $hubAri, operationType: $operationType) @required(action: THROW) {
					asapIssuer @required(action: THROW)
					mediaCollectionName @required(action: THROW)
					mediaToken @required(action: THROW)
					mediaUrl @required(action: THROW)
				}
				pageMediaConfig: hubMediaConfig(ari: $pageAri, operationType: $operationType)
					@skip(if: $skipPageMediaConfig) {
					asapIssuer
					mediaCollectionName
					mediaToken
					mediaUrl
				}
				helpCenterById(helpCenterAri: $hubAri) @optIn(to: "HelpCenterAggBeta") {
					... on HelpCenter {
						type @optIn(to: "HelpCenterTypeTest")
						helpCenterBranding @optIn(to: ["HelpCenterBrandingTest"]) {
							logo {
								fileId
								url
							}
							colors {
								primary
							}

							navigationBar @optIn(to: "HelpCenterNavigationBarTest") {
								backgroundColor
								borderColor
								borderType
								cornerRadius
								padding
								positionType
								textColor
							}
						}
					}
				}
			}
		`,
		bootstrapQueryReference,
	);

	const tenantData =
		useFragment<hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference$key>(
			graphql`
				fragment hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference on Query {
					tenantContexts(cloudIds: [$cloudId]) @include(if: $hubEditMode) {
						cloudUrl
					}
				}
			`,
			tenantContextsQueryReference,
		);

	const cloudUrl = tenantData.tenantContexts?.[0]?.cloudUrl ?? window.location.origin;
	if (!data.helpCenterById) {
		throw new Error('Help center not found');
	}

	const branding = data.helpCenterById?.helpCenterBranding;
	const logo = branding?.logo;
	const primaryColor = branding?.colors?.primary ?? undefined;
	const fileId = logo?.fileId || '';
	const url = logo?.url || '';
	const hubType = data.helpCenterById?.type || '';

	// Convert the logo to a base64 data URL so the signed token embedded in the
	// original URL never expires while the user has the page open.
	const [logoUrl, setLogoUrl] = useState(url);
	const prevUrlRef = useRef(url);
	if (prevUrlRef.current !== url) {
		prevUrlRef.current = url;
		setLogoUrl(url);
	}

	useEffect(() => {
		if (!url || !fg('csm_brand_entity_url_fix')) {
			return;
		}

		let cancelled = false;
		fetch(url)
			.then((res) => res.blob())
			.then((blob) => new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			}))
			.then((base64) => {
				if (!cancelled) {
					setLogoUrl(base64);
				}
			})
			.catch(() => {
				// Silently fall back to the original signed URL if the fetch fails.
			});

		return () => {
			cancelled = true;
		};
	}, [url]);

	const navigationBar = branding?.navigationBar ?? null;
	const navStyleOptions = useMemo<NavigationStyleContextType>(
		() => ({
			options: navigationBar ? mapApiToNavigationOptions(navigationBar, primaryColor) : null,
			setOptions: () => void 0,
		}),
		[navigationBar, primaryColor],
	);

	// When the fix flag is ON, `pageMediaConfig` is omitted from the bootstrap
	// and is instead provided by the page-scoped `Hub` (read mode) and `HubInfo`
	// (edit mode) components via a nested `HubContext.Provider`. When the flag
	// is OFF the bootstrap still fetches it (via `$pageAri`) and provides it
	// here as before.
	const bootstrapPageMediaConfig = fg('hubs_fix_rerendering_bootstrap_page_media')
		? undefined
		: getPageMediaConfigFromBootstrap(data.pageMediaConfig);

	// Memoize the context value so that consumers (e.g. `Hub`) receive a stable
	// reference across re-renders. Without this, every render of `HubBootstrap`
	// produces a new object literal, causing all `useHubContext()` consumers to
	// re-render even when nothing meaningful has changed. This is especially
	// important for the `useMemo` in `Hub` that merges `pageMediaConfig` into
	// the context — its deps array includes `hubContext`, so it needs a stable
	// input to avoid recomputing on every render.
	const hubContextValue = useMemo(
		() => ({
			...(bootstrapPageMediaConfig ? { pageMediaConfig: bootstrapPageMediaConfig } : {}),
			hubMediaConfig: data.hubMediaConfig,
			branding: {
				logo: {
					fileId,
					url: fg('csm_brand_entity_url_fix') ? logoUrl : url,
				},
				colors: {
					primary: primaryColor || DEFAULT_BRANDING_COLOR,
				},
			},
			layout: props.layout,
			user: props.user,
			env: props.env,
			actions: props.actions,
			containerStyling: props.containerStyling,
			renderAvatarMenuOptions: props.renderAvatarMenuOptions,
			renderRightNavActions: props.renderRightNavActions,
			featureFlags: props.featureFlags,
			headerless: props.headerless,
			cloudId,
			cloudUrl,
			hubType,
			isInPreviewMode,
		}),
		[
			bootstrapPageMediaConfig,
			data.hubMediaConfig,
			fileId,
			url,
			logoUrl,
			primaryColor,
			props.layout,
			props.user,
			props.env,
			props.actions,
			props.containerStyling,
			props.renderAvatarMenuOptions,
			props.renderRightNavActions,
			props.featureFlags,
			props.headerless,
			cloudId,
			cloudUrl,
			hubType,
			isInPreviewMode,
		],
	);

	return (
		<HubContext.Provider value={hubContextValue}>
			<NavigationStyleContext.Provider value={navStyleOptions}>
				{children}
			</NavigationStyleContext.Provider>
		</HubContext.Provider>
	);
};

export const HubBootstrapWrapper = ({
	children,
	queryReference,
	...props
}: HubBootstrapWrapperProps): React.JSX.Element | null => {
	const data = usePreloadedQuery<hubBootstrapWrapperQuery>(
		graphql`
			query hubBootstrapWrapperQuery(
				$cloudId: ID!
				$hubEditMode: Boolean!
				$hubAri: ID!
				$pageAri: ID!
				$operationType: HelpCenterMediaConfigOperationType
				$skipPageMediaConfig: Boolean!
			) {
				helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
					...hubBootstrap_AtlassianCompanyHubCommon_bootstrapQueryReference
						@arguments(pageAri: $pageAri, skipPageMediaConfig: $skipPageMediaConfig)
				}
				...hubBootstrap_AtlassianCompanyHubCommon_tenantContextsQueryReference
			}
		`,
		queryReference,
	);

	if (!data.helpCenter) {
		return null;
	}

	return (
		<HubBootstrap
			bootstrapQueryReference={data.helpCenter}
			tenantContextsQueryReference={data}
			{...props}
		>
			{children}
		</HubBootstrap>
	);
};
